import dayjs from "dayjs";

import {
  PAYROLL_DAY_BASIS_OPTIONS,
  type PayrollDayBasis,
} from "@/containers/staff/staff-settings/staff-settings-form";
import { formatINR } from "@/utils/app-formatters";

export type PayrollLopLineExplainerInput = {
  yearMonth: string;
  payrollDayBasis: string;
  monthlyGross: number;
  lopDays: number;
  /** Stored on the line for WORKING_DAYS basis. */
  workingDaysInMonth?: number | null;
  /** Absolute LOP component amount when known; otherwise computed. */
  lopAmount?: number | null;
  presentDays?: number | null;
  absentDays?: number | null;
  leaveDays?: number | null;
  holidayDays?: number | null;
  leaveAvailableDays?: number | null;
};

export type PayrollLopLineExplainer = {
  title: string;
  bodyLines: string[];
  /** Single string for plain Tooltip / print. */
  tooltipText: string;
};

function dayBasisLabel(basis: string): string {
  const option = PAYROLL_DAY_BASIS_OPTIONS.find((o) => o.id === basis);
  return option?.name ?? basis;
}

/** Days used as the daily-rate divisor for this period/line (null if unknown). */
export function resolvePayrollDayDivisor(params: {
  payrollDayBasis: string;
  yearMonth: string;
  workingDaysInMonth?: number | null;
}): number | null {
  const basis = params.payrollDayBasis as PayrollDayBasis;
  if (basis === "FIXED_30") return 30;
  if (basis === "CALENDAR_DAYS") {
    const d = dayjs(`${params.yearMonth}-01`);
    return d.isValid() ? d.daysInMonth() : null;
  }
  if (basis === "WORKING_DAYS") {
    const w = params.workingDaysInMonth;
    return w != null && Number.isFinite(w) && w > 0 ? w : null;
  }
  return null;
}

export function roundInr(amount: number): number {
  return Math.round(amount);
}

function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return String(value);
}

/**
 * Row-specific LOP explainer: days are manager-entered; rupees = daily rate × days.
 */
export function buildPayrollLopLineExplainer(
  input: PayrollLopLineExplainerInput
): PayrollLopLineExplainer {
  const basisName = dayBasisLabel(input.payrollDayBasis);
  const divisor = resolvePayrollDayDivisor({
    payrollDayBasis: input.payrollDayBasis,
    yearMonth: input.yearMonth,
    workingDaysInMonth: input.workingDaysInMonth,
  });

  const lopDays = Number.isFinite(input.lopDays) ? Math.max(0, input.lopDays) : 0;
  const gross = Number.isFinite(input.monthlyGross) ? Math.max(0, input.monthlyGross) : 0;

  const bodyLines: string[] = [];

  if (divisor != null && divisor > 0) {
    const dailyRate = roundInr(gross / divisor);
    const computedLop = roundInr(dailyRate * lopDays);
    const lopAmount =
      input.lopAmount != null && Number.isFinite(input.lopAmount)
        ? Math.abs(input.lopAmount)
        : computedLop;

    bodyLines.push(
      `LOP days for this employee: ${lopDays}`,
      `Daily rate: ${formatINR(dailyRate)} (${formatINR(gross)} ÷ ${divisor} ${basisName.toLowerCase()})`,
      `LOP deduction: ${formatINR(lopAmount)} (${formatINR(dailyRate)} × ${lopDays})`
    );
  } else {
    bodyLines.push(
      `LOP days for this employee: ${lopDays}`,
      `Day basis: ${basisName}. Daily rate = monthly gross ÷ day divisor for the period.`
    );
    if (input.lopAmount != null && Number.isFinite(input.lopAmount)) {
      bodyLines.push(`LOP deduction: ${formatINR(Math.abs(input.lopAmount))}`);
    }
  }

  bodyLines.push(
    `Attendance this month: Present ${formatCount(input.presentDays)} · Absent ${formatCount(input.absentDays)} · Leave ${formatCount(input.leaveDays)} · Holiday ${formatCount(input.holidayDays)} · Leave avail. ${formatCount(input.leaveAvailableDays)}`,
    "LOP days are entered by the manager (not auto-filled from Absent). Half days like 0.5 are allowed."
  );

  return {
    title: "LOP for this employee",
    bodyLines,
    tooltipText: bodyLines.join("\n"),
  };
}

/** Prefer the LOP component amount when present on the line. */
export function lopAmountFromComponents(
  components: Array<{ type: string; amount: number }> | null | undefined
): number | null {
  const lop = components?.find((c) => c.type === "LOP");
  if (!lop || !Number.isFinite(lop.amount)) return null;
  return Math.abs(lop.amount);
}
